import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Box, Stack, CircularProgress, ToggleButton, ToggleButtonGroup, Button, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import GridLayout from '../../components/layout/GridLayout';
import Menu from '../../components/layout/Menu';
import Card from '../../components/ui/Card';

interface NewsItem {
  title: string;
  description: string;
  url: string;
  publishedAt: string;
  source?: { name?: string };
}

export default function NewsPage({ isMenuOpen = false }: { isMenuOpen?: boolean }) {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('ALL');
  const [showCacheWarning, setShowCacheWarning] = useState(false);

  const BRAND_GREEN = '#34de9aab';

  const getQuery = useCallback(() => {
    switch (category) {
      case 'IOT': return 'IoT wearable health device';
      case 'REHAB': return 'rehabilitation physiotherapy recovery';
      case 'AI': return 'AI posture fitness health tech';
      default: return 'wearable health tech OR rehabilitation';
    }
  }, [category]);

  const fetchNews = useCallback(async (forceRefresh = false) => {
    const cacheKey = `news_cache_${category}`;
    const cachedData = localStorage.getItem(cacheKey);

    if (cachedData && !forceRefresh) {
      setNews(JSON.parse(cachedData));
      return;
    }

    setLoading(true);
    setShowCacheWarning(false);

    try {
      // Pointing to your internal Vercel API route
      const url = `/api/getNews?query=${encodeURIComponent(getQuery())}`;
      const response = await fetch(url);
      
      if (!response.ok) throw new Error("Backend API Error");

      const data = await response.json();
      
      if (data.articles) {
        setNews(data.articles);
        localStorage.setItem(cacheKey, JSON.stringify(data.articles));
      }
    } catch (err) {
      console.error("Fetch error:", err);
      if (cachedData) {
        setNews(JSON.parse(cachedData));
        setShowCacheWarning(true);
      }
    } finally {
      setLoading(false);
    }
  }, [getQuery, category]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  return (
    <GridLayout
      isMenuOpen={isMenuOpen}
      left={<Menu />}
      right={
        <Card>
          <Typography variant="h6" sx={{ color: BRAND_GREEN, fontWeight: 'bold' }}>
            Focus Areas
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, lineHeight: 1.8 }}>
            • Wearable health devices<br />
            • IoT rehabilitation<br />
            • AI posture<br />
            • Fitness tech
          </Typography>
        </Card>
      }
      main={
        <Stack spacing={3} sx={{ pb: 5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" sx={{ color: BRAND_GREEN, fontWeight: 'bold' }}>
              Tech & IoT Health News
            </Typography>
            <Button 
              size="small" 
              startIcon={<RefreshIcon />} 
              onClick={() => fetchNews(true)}
              sx={{ color: BRAND_GREEN }}
            >
              Refresh
            </Button>
          </Box>

          <ToggleButtonGroup
            value={category}
            exclusive
            onChange={(_, v) => v && setCategory(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="ALL">All</ToggleButton>
            <ToggleButton value="IOT">IoT</ToggleButton>
            <ToggleButton value="REHAB">Rehab</ToggleButton>
            <ToggleButton value="AI">AI</ToggleButton>
          </ToggleButtonGroup>

          {showCacheWarning && (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              Unable to reach live feed. Showing last successfully loaded news.
            </Alert>
          )}

          {loading && news.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress sx={{ color: BRAND_GREEN }} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {news.map((item, i) => (
                <Box key={i} sx={{ p: 3, border: '1px solid #eee', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ color: BRAND_GREEN, fontWeight: 'bold' }}>
                    {item.source?.name} — {new Date(item.publishedAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>{item.title}</Typography>
                  <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#666' }}>{item.description}</Typography>
                  <a href={item.url} target="_blank" rel="noreferrer" style={{ color: BRAND_GREEN, fontWeight: 'bold', textDecoration: 'none' }}>
                    Read full article →
                  </a>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      }
    />
  );
}